import { Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { CardAttributesProps } from './CardAttributes'

export type OrderViewCardAttribute = CardAttributesProps & {
  sizeXs?: number
  sizeSm?: number
  sizeMd?: number
  sizeLg?: number
}

type Section = {
  subtitle?: string
  attributes?: OrderViewCardAttribute[]
  component?: React.ReactNode
}

type Props = {
  title?: string
  sections: Section[]
  showDividers?: boolean
}

export const SectionedCardWithAttributes = observer((props: Props) => {
  const { title, sections, showDividers = false } = props
  const visibleSections = (sections || []).filter(
    s => (s.attributes && s.attributes.length > 0) || !!s.component,
  )

  return (
    <Paper variant='outlined' sx={{ p: '1rem' }}>
      <Grid container direction='column' spacing={2}>
        {title && (
          <Grid size={12}>
            <Typography variant='subtitle1'>{title}</Typography>
          </Grid>
        )}

        {visibleSections.map((section, idx) => (
          <Grid key={section.subtitle} size={12}>
            {idx > 0 && showDividers && <Divider sx={{ mb: 2 }} />}

            {section.component ? (
              section.component
            ) : (
              <Stack spacing={1}>
                <Typography variant='subtitle2'>{section.subtitle}</Typography>

                <Grid container columnSpacing={4} rowSpacing={2}>
                  {section.attributes!.map((attribute, i) => (
                    <Grid
                      key={`${section.subtitle}-${i}`}
                      size={{
                        xs: attribute.sizeXs ?? 12,
                        sm: attribute.sizeSm ?? attribute.sizeXs ?? 6,
                        md: attribute.sizeMd ?? attribute.sizeSm ?? attribute.sizeXs ?? 6,
                        lg:
                          attribute.sizeLg ??
                          attribute.sizeMd ??
                          attribute.sizeSm ??
                          attribute.sizeXs ??
                          6,
                      }}
                    >
                      <Stack sx={{ width: 'max-content' }}>
                        <Typography variant='body2'>{attribute.label}</Typography>
                        <Typography
                          sx={{ fontWeight: 'bold' }}
                          variant='body1'
                          data-cy={`${title ?? 'card'}-${section.subtitle}-${attribute.label}`.replace(
                            /[\u200B-\u200D\uFEFF]/g,
                            '',
                          )}
                        >
                          {attribute.value}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            )}
          </Grid>
        ))}
      </Grid>
    </Paper>
  )
})
